import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ActivityIcon, TargetIcon, UsersIcon, ArrowRightIcon, MedalIcon, ShieldIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { SectionHeader } from '../components/SectionHeader';
import { ValuesSection } from '../components/ValuesSection';
const activityData = {
  'multi-sports': {
    name: 'Multi-Sports',
    tagline: 'Building All-Round Athletes Through Play',
    description: 'Dynamic multi-sport sessions led by our Olympian coaching team, exposing children to a variety of sports and fundamental movement skills.',
    colorAccent: 'coral' as const,
    icon: <TargetIcon className="w-24 h-24" data-id="element-324" />,
    image: 'https://cdn.magicpatterns.com/uploads/nius5mV4GSJtkuCgu886pQ/Multisports.webp',
    overview: ['Our Multi-Sports programme is designed and delivered by lead coaches Donna Kellogg MBE, Natalie Barton and Sarah Chapman, all Team GB Olympians who bring world-class expertise to every session.', 'By rotating through different sports and activities, children develop a broad athletic foundation. We cover athletics, basketball, cricket, dodgeball, football, gymnastics, tag rugby, tennis and more. This prevents early specialisation burnout and helps children discover the sports they truly love.', 'Our primary focus is on Physical Literacy: the motivation, confidence, physical competence, and knowledge to value and take responsibility for engagement in physical activities for life.']
  },
  netball: {
    name: 'Netball',
    tagline: 'Court Skills, Team Spirit, Confidence',
    description: 'Inclusive netball coaching led by our Olympian team, building court skills, tactical awareness, and team spirit for all abilities.',
    colorAccent: 'green' as const,
    icon: <UsersIcon className="w-24 h-24" data-id="element-325" />,
    image: 'https://cdn.magicpatterns.com/uploads/4gCkFMntbwDcZHYhPNEDey/Netball.jpg',
    overview: ['Our netball programme is led by our team of Olympian coaches: Donna Kellogg MBE, Natalie Barton and Sarah Chapman. Their combined experience at the highest level of sport ensures every session is expertly structured and genuinely inspiring.', 'We focus on building essential court skills, tactical awareness, and an unbreakable team spirit. Netball is a fantastic sport for developing communication, leadership, and resilience, and we create an empowering environment where young players build confidence both on and off the court.', 'Sessions include skills development, guided match play with real-time coaching feedback, netball-specific fitness drills, and team-building exercises to foster strong relationships between players.']
  }
};
export function ActivityPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Redirect to dedicated Badminton page
  if (slug === 'badminton') {
    return <Navigate to="/badminton" replace data-id="element-326" />;
  }
  const activity = slug ? activityData[slug as keyof typeof activityData] : null;
  if (!activity) {
    return <main className="min-h-[70vh] flex items-center justify-center bg-navy-50 px-4 pt-20" data-id="element-327">
        <div className="text-center max-w-lg" data-id="element-328">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-navy-100 text-navy-400 mb-6" data-id="element-329">
            <ActivityIcon className="w-10 h-10" data-id="element-330" />
          </div>
          <h1 className="text-4xl font-bold text-navy-900 mb-4" data-id="element-331">
            Programme Not Found
          </h1>
          <p className="text-lg text-navy-600 mb-8" data-id="element-332">
            We couldn't find the activity you're looking for. It may have been
            moved or no longer exists.
          </p>
          <Link to="/" data-id="element-333">
            <Button variant="primary" className="inline-flex items-center" data-id="element-334">
              <ArrowRightIcon className="w-4 h-4 mr-2 rotate-180" data-id="element-335" />
              Back to Home
            </Button>
          </Link>
        </div>
      </main>;
  }
  return <main className="bg-white pt-20" data-id="element-336">
      {/* Hero Section */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 min-h-[320px] md:min-h-[400px] overflow-hidden bg-brand-blue" data-id="element-337">
        {activity.image && <img src={activity.image} alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" data-id="element-338" />}
        <div className="absolute inset-0 bg-brand-blue/80" data-id="element-339"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-340">
          <div className="mb-8" data-id="element-341">
            <nav aria-label="Breadcrumb" className="flex text-sm font-medium text-white/80 mb-8" data-id="element-342">
              <Link to="/" className="hover:text-white transition-colors" data-id="element-343">
                Home
              </Link>
              <span className="mx-2" data-id="element-344">/</span>
              <span className="cursor-default" data-id="element-345">Activities</span>
              <span className="mx-2" data-id="element-346">/</span>
              <span className="text-white" data-id="element-347">{activity.name}</span>
            </nav>
          </div>

          <div className="max-w-3xl" data-id="element-348">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-349">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6 leading-tight" data-id="element-350">
                {activity.tagline}
              </h1>
              <p className="text-xl text-white/90 mb-10 max-w-2xl leading-relaxed" data-id="element-351">
                {activity.description}
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-352">
                <Button variant="primary" size="lg" href="/book" data-id="element-353">
                  Book Now
                </Button>
                <Button variant="secondary" size="lg" onClick={() => document.getElementById('contact')?.scrollIntoView({
                behavior: 'smooth'
              })} className="border-white text-white hover:bg-white/20" data-id="element-354">
                  Contact Us
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-355">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-356">
          <div data-id="element-357">
            <SectionHeader eyebrow="OVERVIEW" title={`About Our ${activity.name} Sessions`} align="left" data-id="element-358" />
            <div className="space-y-6 text-lg text-navy-600" data-id="element-359">
              {activity.overview.map((paragraph, i) => <p key={i} className="leading-relaxed" data-id="element-360">
                  {paragraph}
                </p>)}
            </div>
          </div>

          <div className="bg-navy-50 rounded-2xl p-8 md:p-12 border border-navy-100 relative overflow-hidden" data-id="element-361">
            <div className={`absolute top-0 left-0 w-2 h-full ${activity.colorAccent === 'blue' ? 'bg-brand-blue' : activity.colorAccent === 'coral' ? 'bg-brand-coral' : 'bg-brand-green'}`} data-id="element-362"></div>
            <h3 className="text-2xl font-bold text-navy-900 mb-8" data-id="element-363">
              Programme Highlights
            </h3>
            <ul className="space-y-6" data-id="element-364">
              <li className="flex items-start" data-id="element-365">
                <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center mr-4 flex-shrink-0 text-gold-500" data-id="element-366">
                  <MedalIcon className="w-5 h-5" data-id="element-367" />
                </div>
                <div data-id="element-368">
                  <h4 className="font-bold text-navy-900" data-id="element-369">
                    Led by Our Olympian Coaches
                  </h4>
                  <p className="text-navy-600 text-sm mt-1" data-id="element-370">
                    Donna Kellogg MBE, Natalie Barton and Sarah Chapman bring
                    elite experience to every session.
                  </p>
                </div>
              </li>
              <li className="flex items-start" data-id="element-371">
                <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center mr-4 flex-shrink-0 text-gold-500" data-id="element-372">
                  <UsersIcon className="w-5 h-5" data-id="element-373" />
                </div>
                <div data-id="element-374">
                  <h4 className="font-bold text-navy-900" data-id="element-375">Small Group Sizes</h4>
                  <p className="text-navy-600 text-sm mt-1" data-id="element-376">
                    High coach-to-player ratio ensures individual attention for
                    every child.
                  </p>
                </div>
              </li>
              <li className="flex items-start" data-id="element-377">
                <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center mr-4 flex-shrink-0 text-gold-500" data-id="element-378">
                  <ShieldIcon className="w-5 h-5" data-id="element-379" />
                </div>
                <div data-id="element-380">
                  <h4 className="font-bold text-navy-900" data-id="element-381">Safe & Inclusive</h4>
                  <p className="text-navy-600 text-sm mt-1" data-id="element-382">
                    A welcoming environment for all abilities and backgrounds.
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Olympic Values */}
      <ValuesSection ctaLabel="Book a Session" ctaHref="/book" data-id="element-383" />
    </main>;
}